// src/components/BacktestTradeList.tsx
import React from 'react';
import { useAppStore } from '../state/store';
import type { SimpleBacktestTrade } from '../state/types';
import { formatTimestampByBarType } from '../utils/tzutils';
import { callRefresh, callEquityCurveForSet } from '../api/refresh';
import { expandExpressionGraph } from '../utils/expandExpressionGraph';


const BacktestTradeList: React.FC = () => {
  const result = useAppStore((s) => s.backtestResult);
  const timezone = useAppStore((s) => s.timezone);
  const barType = useAppStore((s) => s.bar_type);
  const selectedKey = useAppStore((s) => s.selectedTradeSetKey);
  const setSelectedKey = useAppStore((s) => s.setSelectedTradeSetKey);
  const selectedTradeIndex = useAppStore((s) => s.selectedTradeIndex);
  const setSelectedTradeIndex = useAppStore((s) => s.setSelectedTradeIndex);
  const backtestTradePricesView = useAppStore((s) => s.backtestTradePricesView);
  const setBacktestTradePricesView = useAppStore((s) => s.setBacktestTradePricesView);
  const backtestParamsDirtySinceRun = useAppStore((s) => s.backtestParamsDirtySinceRun);
  const lastBacktestRequest = useAppStore((s) => s.lastBacktestRequest);
  const setBacktestLoading = useAppStore((s) => s.setBacktestLoading);
  const loadFromRefreshResponse = useAppStore((s) => s.loadFromRefreshResponse);
  const setLastBacktestRequest = useAppStore((s) => s.setLastBacktestRequest);
  const setBacktestParamsDirtySinceRun = useAppStore((s) => s.setBacktestParamsDirtySinceRun);
  const applyEquityCurveForSetResponse = useAppStore((s) => s.applyEquityCurveForSetResponse);
  const instruments = useAppStore((s) => s.instruments);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const timestamps = result?.timestamps ?? [];

  if (!result) return null;

  const isExpressionHold = Array.isArray(result.expression_hold_trades);
  const constantHoldOptions = isExpressionHold
    ? []
    : Object.keys(result.constant_hold_trades)
        .map(Number)
        .sort((a, b) => a - b);

  const selectedTrades: SimpleBacktestTrade[] = isExpressionHold
    ? result.expression_hold_trades ?? []
    : selectedKey === 'exit'
      ? result.exit_on_first_profit_trades
      : result.constant_hold_trades[parseInt(selectedKey)] ?? [];

  const showDividendColumn =
    backtestTradePricesView === 'unadjusted' &&
    selectedTrades.some((t) => (t.dividend_dollars ?? 0) > 0);
  const columnCount = 9 + (showDividendColumn ? 1 : 0);

  function formatTimestamp(ts: string): string {
    //return formatTimestampUTC(ts, allSame);
    return formatTimestampByBarType(ts, barType, timezone);
  }

  const onRowClick = (idx: number) => {
    setSelectedTradeIndex(selectedTradeIndex === idx ? null : idx);
  };

  const onTradeSetChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newKey = e.target.value;
    setSelectedKey(newKey);
    if (!result || result.expression_hold_trades != null) return;
    const dirty = backtestParamsDirtySinceRun || !lastBacktestRequest;
    if (dirty) {
      setBacktestLoading(true);
      try {
        const state = useAppStore.getState();
        const expandedGraph = expandExpressionGraph(
          { rootId: state.expressionGraph.rootId, expressions: state.expressionGraph.expressions },
          state.constants
        );
        const bs = state.backtestSettings;
        const bt = state.backtestSettings.backtestType ?? 'simple';
        const backtestRequest = {
          expressionGraph: expandedGraph,
          num_bars: 5,
          trade_direction: bs.tradeDirection,
          num_units: 1,
          backtest_type: bt,
          entry_type: bs.entryType,
          exit_type: bs.exitType,
          position_sizing: bs.positionSizing,
          fixed_shares: bs.positionSizing === 'fixed_shares' ? bs.fixedShares : undefined,
          fixed_amount_dollars: bs.positionSizing === 'fixed_amount' ? bs.fixedAmountDollars : undefined,
          fixed_fraction_pct: bs.positionSizing === 'fixed_fraction' ? bs.fixedFractionPct : undefined,
          initial_capital: bs.positionSizing === 'fixed_fraction' ? bs.initialCapital : undefined,
          allow_fractional_shares: bs.allowFractionalShares,
          min_lot_size: bs.minLotSize !== '' && !bs.allowFractionalShares ? Number(bs.minLotSize) : undefined,
          simpleTradeSet: newKey,
          ...(bt === 'exit_expression'
            ? { exitExpressionGraph: expandExpressionGraph(state.exitExpressionGraph, state.constants) }
            : {}),
        };
        const request = {
          refresh: 'backtest' as const,
          instruments: state.instruments,
          primaryInstrumentId: state.primaryInstrumentId ?? '',
          indicators: state.indicators,
          backtestRequest,
        };
        const response = await callRefresh(request);
        loadFromRefreshResponse(response);
        setLastBacktestRequest(backtestRequest);
        setBacktestParamsDirtySinceRun(false);
      } catch (err) {
        console.error('Full refresh on trade-set change failed:', err);
      } finally {
        setBacktestLoading(false);
      }
    } else {
      setBacktestLoading(true);
      try {
        const req = {
          simpleTradeSet: newKey,
          result,
          backtestRequest: lastBacktestRequest!,
          instruments,
          primaryInstrumentId: primaryInstrumentId ?? '',
        };
        const response = await callEquityCurveForSet(req);
        applyEquityCurveForSetResponse(response);
      } catch (err) {
        console.error('Equity curve for set failed:', err);
      } finally {
        setBacktestLoading(false);
      }
    }
  };

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="tradeSet" className="font-semibold">
            Trade Set:
          </label>
          {isExpressionHold ? (
            <span id="tradeSet" className="text-ink">
              Expression Hold ({(result.expression_hold_trades ?? []).length} trades)
            </span>
          ) : (
            <select
              id="tradeSet"
              className="border border-rule bg-newsprint-panel text-ink px-2 py-1 rounded-sm"
              value={selectedKey}
              onChange={onTradeSetChange}
            >
              <option value="exit">
                Exit on First Profit ({result.exit_on_first_profit_trades.length} trades)
              </option>
              {constantHoldOptions.map((bars) => (
                <option key={bars} value={bars.toString()}>
                  {bars} bars ({result.constant_hold_trades[bars].length} trades)
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="font-semibold">
            Prices:
          </label>
          <div className="flex items-center gap-1">
            {(['adjusted', 'unadjusted'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setBacktestTradePricesView(mode)}
                  className={
                    'px-2 py-1 text-xs rounded-sm border transition-colors ' +
                    (backtestTradePricesView === mode
                      ? 'bg-ink text-newsprint border-ink'
                      : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark')
                  }
                >
                  {mode === 'adjusted' ? 'Adjusted' : 'Unadjusted'}
                </button>
            ))}
          </div>
        </div>
        {selectedTradeIndex !== null && (
          <button
            type="button"
            className="px-2 py-1 text-sm border border-rule bg-newsprint-panel text-ink rounded-sm hover:bg-newsprint-dark"
            onClick={() => setSelectedTradeIndex(null)}
          >
            Clear selection
          </button>
        )}
      </div>

      <div className="overflow-auto">
        <table className="min-w-full text-sm border border-rule border-collapse">
          <thead>
            <tr className="bg-newsprint-dark">
              <th className="border border-rule px-2 py-1">Entry</th>
              <th className="border border-rule px-2 py-1">Exit</th>
              <th className="border border-rule px-2 py-1"># Bars</th>
              <th className="border border-rule px-2 py-1">Direction</th>
              <th className="border border-rule px-2 py-1">Entry Price</th>
              <th className="border border-rule px-2 py-1">Exit Price</th>
              {showDividendColumn && (
                <th className="border border-rule px-2 py-1">Dividend</th>
              )}
              <th className="border border-rule px-2 py-1">Drawdown</th>
              <th className="border border-rule px-2 py-1">Run-up</th>
              <th className="border border-rule px-2 py-1">Return</th>
            </tr>
          </thead>
          <tbody>
            {selectedTrades.map((trade, idx) => {
              const isInProgress = trade.in_progress === true;
              const isProfit = trade.return_fraction > 0;
              const isLoss = trade.return_fraction < 0;
              const isSelected = selectedTradeIndex === idx;

              const alternating = idx % 2 === 0 ? 'bg-newsprint' : 'bg-newsprint-panel';
              const outcomeColor = isInProgress
                ? 'bg-table-inProgress'
                : isProfit
                  ? 'bg-table-win'
                  : isLoss
                    ? 'bg-table-loss'
                    : '';
              const selectedClass = isSelected
                ? 'border-l-4 border-l-accent cursor-pointer'
                : 'cursor-pointer';

              const entryPrice =
                backtestTradePricesView === 'adjusted' && trade.entry_price_adj != null
                  ? trade.entry_price_adj
                  : trade.entry_price;
              const exitPrice =
                backtestTradePricesView === 'adjusted' && trade.exit_price_adj != null
                  ? trade.exit_price_adj
                  : trade.exit_price;

              return (
                <tr
                  key={idx}
                  role="button"
                  tabIndex={0}
                  onClick={() => onRowClick(idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(idx);
                    }
                  }}
                  className={`${alternating} ${outcomeColor} ${selectedClass}`}
                >
                  <td className="border border-rule px-2 py-1 text-center">
                    {timestamps[trade.entry_bar] ? formatTimestamp(timestamps[trade.entry_bar]) : `#${trade.entry_bar}`}
                  </td>
                  <td className="border border-rule px-2 py-1 text-center">
                    <span className="inline-flex items-center gap-1.5">
                      {timestamps[trade.exit_bar] ? formatTimestamp(timestamps[trade.exit_bar]) : `#${trade.exit_bar}`}
                      {trade.had_split && (
                        <span
                          className="inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 text-xs font-medium border border-rule rounded-sm bg-newsprint-panel text-ink"
                          title="Split occurred during trade"
                        >
                          S
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="border border-rule px-2 py-1 text-center">
                    {trade.exit_bar - trade.entry_bar}
                  </td>
                  <td className="border border-rule px-2 py-1 text-center">{trade.trade_direction}</td>
                  <td className="border border-rule px-2 py-1 text-right">{entryPrice.toFixed(2)}</td>
                  <td className="border border-rule px-2 py-1 text-right">{exitPrice.toFixed(2)}</td>
                  {showDividendColumn && (
                    <td className="border border-rule px-2 py-1 text-right">
                      {(trade.dividend_dollars ?? 0) > 0
                        ? (trade.dividend_dollars ?? 0).toFixed(2)
                        : '—'}
                    </td>
                  )}
                  <td className="border border-rule px-2 py-1 text-right">
                    {(trade.max_drawdown * 100).toFixed(2)}%
                  </td>
                  <td className="border border-rule px-2 py-1 text-right">
                    {(trade.max_runup * 100).toFixed(2)}%
                  </td>
                  <td className="border border-rule px-2 py-1 text-right">
                    {(trade.return_fraction * 100).toFixed(2)}%
                  </td>
                </tr>
              );
            })}
            {selectedTrades.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="text-center text-ink/70 py-4 border border-rule">
                  No trades to display.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BacktestTradeList;